import { checkNameField, flashRowSaved } from './feedback-controller.js';
import { readErrorMessage } from '../../components/write-request.js';
import { submitDictionaryMutation } from './mutation-request.js';
import { syncReturnQueries } from './query-state.js';

const UNKNOWN_MESSAGE = 'Could not confirm the change was saved. Reload the page before trying again.';
const REFRESH_FAILED_MESSAGE = 'Saved, but the list could not be refreshed. Reload the page.';

/**
 * Every failed rename is told in the row's own slot. A taken name (`duplicate`) also marks the field; a
 * generic failure (`write`) leaves it valid and goes with the next typing or the next save. A stored rename
 * whose list could not be re-read (`refresh`) goes with the next typing only: a later save of the row does
 * not make the list on screen current.
 */
function showRowWriteError(row, message, duplicateName, kind = duplicateName ? 'duplicate' : 'write') {
    const input = row.querySelector('[data-dictionary-input]');
    const error = row.querySelector('[data-dictionary-name-error]');
    if (!(error instanceof HTMLElement)) {
        return;
    }

    error.dataset.dictionaryWriteError = kind;
    if (duplicateName && input instanceof HTMLInputElement) {
        input.setAttribute('aria-invalid', 'true');
    }
    error.textContent = message;
    error.hidden = false;
}

function clearRowWriteError(row, { keepRefresh = false } = {}) {
    const error = row?.querySelector?.('[data-dictionary-name-error]');
    const kind = error?.dataset?.dictionaryWriteError;
    if (!(error instanceof HTMLElement) || (kind !== 'write' && (kind !== 'refresh' || keepRefresh))) {
        return;
    }

    delete error.dataset.dictionaryWriteError;
    error.textContent = '';
    error.hidden = true;
}

/** Focus lost while the row was busy (its `Save` disabled) goes back to the row's field; a newer one stays. */
function recoverRowFocus(row) {
    const input = row.querySelector('[data-dictionary-input]');
    const active = row.ownerDocument?.activeElement ?? null;
    if (
        input instanceof HTMLInputElement
        && (active === null || active === row.ownerDocument?.body || active.isConnected === false)
    ) {
        input.focus();
    }
}

function syncRowState(row, input = row.querySelector('[data-dictionary-input]')) {
    const saveButton = row.querySelector('[data-dictionary-save]');
    const revertButton = row.querySelector('[data-dictionary-revert]');
    if (
        !(input instanceof HTMLInputElement)
        || !(saveButton instanceof HTMLButtonElement)
        || !(revertButton instanceof HTMLButtonElement)
    ) {
        return false;
    }

    const hasChanges = input.value !== input.defaultValue;
    saveButton.disabled = !hasChanges;
    revertButton.disabled = !hasChanges;
    row.dataset.dictionaryState = hasChanges ? 'dirty' : 'clean';

    return true;
}

/** A row with its save in flight is read-only and cannot be submitted again; the marker lives on the row. */
function setRowPending(row, pending) {
    const input = row.querySelector('[data-dictionary-input]');
    const saveButton = row.querySelector('[data-dictionary-save]');
    const revertButton = row.querySelector('[data-dictionary-revert]');
    if (pending) {
        row.setAttribute('aria-busy', 'true');
    } else {
        row.removeAttribute('aria-busy');
    }
    const unchanged = !(input instanceof HTMLInputElement) || input.value === input.defaultValue;
    if (input instanceof HTMLInputElement) {
        input.readOnly = pending;
    }
    if (saveButton instanceof HTMLButtonElement) {
        saveButton.disabled = pending || unchanged;
        saveButton.textContent = pending ? 'Saving...' : 'Save';
    }
    if (revertButton instanceof HTMLButtonElement) {
        revertButton.disabled = pending || unchanged;
    }
}

/**
 * Unsaved edits outlive a re-read of the rows: every dirty row of `current` hands its text - and its error
 * message and invalid mark, which clear only on correction or retry - to the row of the same entry in
 * `replacement`, which has not been inserted yet. A row whose save is waiting its turn is
 * carried with its lock, so it is sent from the new row and cannot be sent twice. The row whose save is being
 * committed is unlocked and clean by then, so its stored name comes from `replacement`.
 */
export function carryDictionaryDrafts(current, replacement) {
    const drafts = new Map();
    current.querySelectorAll('[data-dictionary-id]').forEach((item) => {
        const row = item.querySelector('[data-dictionary-row]');
        const input = row?.querySelector('[data-dictionary-input]');
        const pending = row?.getAttribute('aria-busy') === 'true';
        if (
            input instanceof HTMLInputElement
            && (row.dataset.dictionaryState === 'dirty' || pending)
        ) {
            const error = row.querySelector('[data-dictionary-name-error]');
            drafts.set(item.dataset.dictionaryId, {
                value: input.value,
                pending,
                invalid: input.getAttribute('aria-invalid') === 'true',
                // The row's message (a failed write or an empty name) goes with its draft.
                error: error instanceof HTMLElement && !error.hidden
                    ? { text: error.textContent, kind: error.dataset.dictionaryWriteError }
                    : null,
            });
        }
    });
    replacement.querySelectorAll('[data-dictionary-id]').forEach((item) => {
        const row = item.querySelector('[data-dictionary-row]');
        const input = row?.querySelector('[data-dictionary-input]');
        const draft = drafts.get(item.dataset.dictionaryId);
        if (draft === undefined || !(input instanceof HTMLInputElement)) {
            return;
        }

        input.value = draft.value;
        syncRowState(row);
        if (draft.invalid) {
            input.setAttribute('aria-invalid', 'true');
        }
        const error = row.querySelector('[data-dictionary-name-error]');
        if (draft.error !== null && error instanceof HTMLElement) {
            error.textContent = draft.error.text;
            if (draft.error.kind !== undefined) {
                error.dataset.dictionaryWriteError = draft.error.kind;
            }
            error.hidden = false;
        }
        if (draft.pending) {
            setRowPending(row, true);
        }
    });
}

export function initDictionaryRows({
    root,
    windowRef,
    fetchImpl = globalThis.fetch,
    commitRefresh,
    onSectionStateChange = () => {},
    parseSection = (html, type) => {
        const parsed = new DOMParser().parseFromString(html, 'text/html');

        return parsed.querySelector(`[data-dictionary-section][data-dictionary-type="${type}"]`);
    },
}) {
    const dictionaryLabels = { hardware: 'Hardware', platform: 'Platform', tag: 'Tag' };
    const saveQueues = new Map();

    root.addEventListener('input', (event) => {
        const input = event.target;
        if (!(input instanceof HTMLInputElement) || !input.matches('[data-dictionary-input]')) {
            return;
        }

        const row = input.closest('[data-dictionary-row]');
        if (!(row instanceof HTMLFormElement) || !syncRowState(row, input)) {
            return;
        }
        clearRowWriteError(row);
        if (input.hasAttribute('aria-invalid')) {
            checkNameField(input, row.querySelector('[data-dictionary-name-error]'));
        }

        const section = row.closest('[data-dictionary-section]');
        if (section instanceof HTMLElement) {
            onSectionStateChange(
                section,
                section.querySelector('[data-dictionary-state="dirty"]') !== null,
            );
        }
    });

    root.addEventListener('click', (event) => {
        const target = event.target;
        if (!(target instanceof Element)) {
            return;
        }

        const revertButton = target.closest('[data-dictionary-revert]');
        if (!(revertButton instanceof HTMLButtonElement)) {
            return;
        }

        const row = revertButton.closest('[data-dictionary-row]');
        const input = row?.querySelector('[data-dictionary-input]');
        if (row instanceof HTMLFormElement && input instanceof HTMLInputElement) {
            input.value = input.defaultValue;
            input.dispatchEvent(new Event('input', { bubbles: true }));
            input.focus();
        }
    });

    root.addEventListener('submit', async (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement) || !form.matches('[data-dictionary-row]')) {
            return;
        }

        const input = form.querySelector('[data-dictionary-input]');
        if (
            !(input instanceof HTMLInputElement)
            || input.value === input.defaultValue
            || !checkNameField(input, form.querySelector('[data-dictionary-name-error]'))
        ) {
            event.preventDefault();

            return;
        }

        const section = form.closest('[data-dictionary-section]');
        const type = String(section?.dataset?.dictionaryType ?? '');
        const label = dictionaryLabels[type];
        if (
            !(section instanceof HTMLElement)
            || label === undefined
            || typeof fetchImpl !== 'function'
            || typeof commitRefresh !== 'function'
        ) {
            return;
        }

        event.preventDefault();
        if (form.getAttribute('aria-busy') === 'true') {
            return;
        }

        // Another save of this section re-reads the list before this one is sent, so the row and section
        // this request answers to are looked up again by entry.
        const entryId = String(form.closest('[data-dictionary-id]')?.dataset?.dictionaryId ?? '');
        const liveForm = () => (entryId === ''
            ? null
            : root.querySelector(`[data-dictionary-id="${entryId}"] [data-dictionary-row]`)) ?? form;
        const liveSection = () => liveForm().closest('[data-dictionary-section]') ?? section;
        // Locked from the press, while it waits its turn too: what is sent is what was on screen then.
        setRowPending(form, true);
        // An earlier `Saved` of this row is over once a new save is pressed: a failure must not sit beside it.
        const savedMark = form.querySelector('[data-dictionary-row-saved]');
        if (savedMark instanceof HTMLElement) {
            savedMark.hidden = true;
        }

        const save = async () => {
            const sentForm = liveForm();
            clearRowWriteError(sentForm, { keepRefresh: true });
            syncReturnQueries(sentForm, windowRef);
            // Before a success status only: this save still holds the row's lock.
            const fail = (message, duplicateName = false) => {
                const row = liveForm();
                setRowPending(row, false);
                syncRowState(row);
                showRowWriteError(row, message, duplicateName);
                recoverRowFocus(row);
            };

            let response;
            try {
                response = await submitDictionaryMutation(sentForm, type, windowRef, fetchImpl);
            } catch {
                fail(UNKNOWN_MESSAGE);

                return;
            }
            if (!response.ok) {
                fail(
                    await readErrorMessage(response, `${label} update failed. Try again.`),
                    response.status === 409,
                );

                return;
            }

            // A success status is a stored rename: the sent name becomes the row's baseline and the row is
            // unlocked and clean, so it is not carried into markup that already holds the stored name.
            // Typing into it from here on - even the old name back - is a new draft and is carried.
            const savedRow = liveForm();
            const savedInput = savedRow.querySelector('[data-dictionary-input]');
            if (savedInput instanceof HTMLInputElement) {
                savedInput.defaultValue = savedInput.value;
            }
            setRowPending(savedRow, false);
            syncRowState(savedRow);

            let committedSection = null;
            try {
                const replacement = parseSection(await response.text(), type);
                if (replacement instanceof HTMLElement && replacement.dataset.dictionaryType === type) {
                    committedSection = await commitRefresh(liveSection(), replacement);
                }
            } catch {
                committedSection = null;
            }
            if (!(committedSection instanceof HTMLElement)) {
                // The row was released at the success status; a lock on it now belongs to a newer save of
                // it, waiting its turn, so it is left alone and focus is not taken from it.
                const row = liveForm();
                showRowWriteError(row, REFRESH_FAILED_MESSAGE, false, 'refresh');
                if (row.getAttribute('aria-busy') !== 'true') {
                    recoverRowFocus(row);
                }

                return;
            }

            // The rename is confirmed at its row; the input keeps the focus the press left there.
            const committedItem = entryId === ''
                ? null
                : committedSection.querySelector(`[data-dictionary-id="${entryId}"]`);
            flashRowSaved(committedItem?.querySelector('[data-dictionary-row-saved]'), windowRef);
            const committedInput = committedItem?.querySelector('[data-dictionary-input]');
            const heading = committedSection.querySelector('h2');
            if (committedInput instanceof HTMLElement) {
                committedInput.focus();
            } else if (heading instanceof HTMLElement) {
                heading.setAttribute('tabindex', '-1');
                heading.focus();
            }
        };

        // One save of a section at a time, each answered and re-read before the next is sent: two in flight
        // could come back in either order, and the older list would then overwrite the newer save.
        const turn = (saveQueues.get(type) ?? Promise.resolve()).then(save);
        saveQueues.set(type, turn);
        await turn;
    });
}
